
import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Languages, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AIHybridService } from "@/utils/aiHybridService";
import { useIsMobile } from "@/hooks/use-mobile";

const languages = [
  { value: "hindi", label: "Hindi" },
  { value: "tamil", label: "Tamil" },
  { value: "marathi", label: "Marathi" },
  { value: "telugu", label: "Telugu" },
  { value: "bengali", label: "Bengali" },
  { value: "gujarati", label: "Gujarati" },
  { value: "kannada", label: "Kannada" },
  { value: "punjabi", label: "Punjabi" },
  { value: "hinglish", label: "Hinglish" }
];

const AITranslator: React.FC = () => {
  const [text, setText] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('');
  const [translation, setTranslation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { isMobile, isSmallMobile } = useIsMobile();

  const handleTranslate = async () => {
    if (!text.trim() || !targetLanguage) {
      toast.error('Please enter text and select a target language');
      return;
    }

    setIsLoading(true);
    try {
      const result = await AIHybridService.processText(text, 'translate', { targetLanguage });
      setTranslation(result);
      toast.success('Text translated successfully');
    } catch (error) {
      console.error('Error translating text:', error);
      toast.error('Failed to translate text');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className={`w-full mx-auto ${isMobile ? 'p-2 sm:p-3' : 'p-4'} max-w-2xl`}>
      <div className={`flex items-center gap-2 mb-3 ${isMobile ? 'pb-2' : 'pb-3'} border-b`}>
        <Languages className={`${isSmallMobile ? 'w-4 h-4' : 'w-5 h-5'} text-purple-600`} />
        <h2 className={`${isSmallMobile ? 'text-base' : 'text-lg'} font-semibold`}>AI Translator</h2>
      </div>

      <div className={`space-y-3 ${isSmallMobile ? 'space-y-2' : ''}`}>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Enter text to translate..."
          className={isSmallMobile ? 'text-sm min-h-[80px]' : ''}
          rows={isMobile ? 3 : 4}
        />

        <Select
          value={targetLanguage}
          onValueChange={setTargetLanguage}
        >
          <SelectTrigger className={isSmallMobile ? 'h-8 text-xs' : ''}>
            <SelectValue placeholder="Select target language" />
          </SelectTrigger>
          <SelectContent>
            {languages.map((lang) => (
              <SelectItem key={lang.value} value={lang.value} className={isSmallMobile ? 'text-xs' : ''}>
                {lang.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          onClick={handleTranslate}
          disabled={isLoading || !text.trim() || !targetLanguage}
          className={`w-full ${isSmallMobile ? 'h-8 text-xs py-0' : ''}`}
        >
          {isLoading ? (
            <Loader2 className={`${isSmallMobile ? 'w-3 h-3' : 'w-4 h-4'} animate-spin mr-2`} />
          ) : (
            <Languages className={`${isSmallMobile ? 'w-3 h-3' : 'w-4 h-4'} mr-2`} />
          )}
          Translate
        </Button>

        {translation && (
          <Card className={`${isMobile ? 'p-2 sm:p-3' : 'p-4'} bg-gray-50 dark:bg-gray-800`}>
            <h3 className={`font-semibold mb-2 ${isSmallMobile ? 'text-sm' : ''}`}>Translation:</h3>
            <p className={`whitespace-pre-wrap ${isSmallMobile ? 'text-xs' : ''}`}>{translation}</p>
          </Card>
        )}
      </div>
    </Card>
  );
};

export default AITranslator;
